import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import intakeAPI from '../../api/intake';
import { getApiErrorMessage } from '../../utils/errorUtils';

const formatValue = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  return String(value);
};

const PatientIntakeDetails = () => {
  const { id, intakeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [intake, setIntake] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await intakeAPI.getByPatientAndId(id, intakeId);
        setIntake(data);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Не удалось загрузить анкету'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, intakeId]);

  const steps = useMemo(() => intake?.schema?.steps || [], [intake]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/patients/${id}`)}>
          Назад к пациенту
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {intake?.title || 'Анкета пациента'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Статус: {intake?.status}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Отправлена: {intake?.submittedAt ? format(new Date(intake.submittedAt), 'dd MMM yyyy HH:mm') : '—'}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Риск-скор: <strong>{intake?.riskScore || 0}</strong>
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(intake?.riskFlags || []).length > 0 ? (
            intake.riskFlags.map((flag) => <Chip key={flag} label={flag} color="warning" size="small" />)
          ) : (
            <Chip label="Нет красных флагов" color="default" size="small" />
          )}
        </Box>
      </Paper>

      {steps.map((step) => (
        <Paper key={step.id} elevation={1} sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {step.title}
          </Typography>
          {step.description && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
              {step.description}
            </Typography>
          )}
          {(step.sections || []).map((section) => (
            <Box key={section.id} sx={{ mb: 2 }}>
              {section.title && (
                <Typography variant="subtitle1" fontWeight="bold">
                  {section.title}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              {(section.questions || []).map((question) => (
                <Box key={question.id} sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {question.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{question.label}:</strong> {formatValue(intake?.answers?.[question.id])}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Paper>
      ))}
    </Container>
  );
};

export default PatientIntakeDetails;
